"use strict";

import atIcon from "@tabler/icons/outline/at.svg";
import banIcon from "@tabler/icons/outline/ban.svg";
import circleXIcon from "@tabler/icons/outline/circle-x.svg";
import clipboardCopyIcon from "@tabler/icons/outline/clipboard-copy.svg";
import dotsIcon from "@tabler/icons/outline/dots.svg";
import flagIcon from "@tabler/icons/outline/flag.svg";
import messagesIcon from "@tabler/icons/outline/messages.svg";
import searchIcon from "@tabler/icons/outline/search.svg";
import settingsIcon from "@tabler/icons/outline/settings.svg";
import uploadIcon from "@tabler/icons/outline/upload.svg";
import userIcon from "@tabler/icons/outline/user.svg";
import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import {
  useBlockAccount,
  useUnmuteAccount,
  useUnblockAccount,
} from "@/api/accounts";
import { useGetOrCreateChatByAccount } from "@/api/chats";
import DropdownMenu, { type Menu } from "@/components/dropdown-menu";
import { Avatar, Badge, HStack, IconButton } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useComposer } from "@/contexts/composer-context";
import { useModalContext } from "@/contexts/modal-context";
import { ReportableEntities } from "@/contexts/reports-context";
import ActionButton from "@/features/ui/components/action-button";
import SubscriptionButton from "@/features/ui/components/subscription-button";
import { type Account, type Attachment } from "@/schemas";
import toast from "@/toast";
import copy from "@/utils/copy";

import HeaderImage from "./header-image";

const messages = defineMessages({
  edit_profile: { id: "account.edit_profile", defaultMessage: "Edit profile" },
  search: { id: "account.search", defaultMessage: "Search @{name}" },
  mention: { id: "account.mention", defaultMessage: "Mention" },
  chat: { id: "account.chat", defaultMessage: "Chat with @{name}" },
  unmute: { id: "account.unmute", defaultMessage: "Unmute @{name}" },
  block: { id: "account.block", defaultMessage: "Block @{name}" },
  unblock: { id: "account.unblock", defaultMessage: "Unblock @{name}" },
  mute: { id: "account.mute", defaultMessage: "Mute @{name}" },
  report: { id: "account.report", defaultMessage: "Report @{name}" },
  copy: { id: "account.copy", defaultMessage: "Copy link to profile" },
  copied: { id: "copy.success", defaultMessage: "Copied to clipboard!" },
  share: { id: "account.share", defaultMessage: "Share @{name}'s profile" },
  media: { id: "account.media", defaultMessage: "Media" },
  preferences: {
    id: "navigation_bar.preferences",
    defaultMessage: "Preferences",
  },
  blocks: { id: "navigation_bar.blocks", defaultMessage: "Blocks" },
  mutes: { id: "navigation_bar.mutes", defaultMessage: "Mutes" },
  blockConfirm: { id: "confirmations.block.confirm", defaultMessage: "Block" },
  blockAndReport: {
    id: "confirmations.block.block_and_report",
    defaultMessage: "Block & Report",
  },
});

interface HeaderProps {
  account?: Account;
}

const Header: React.FC<HeaderProps> = ({ account }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { currentAccount } = useAuth();
  const { openModal } = useModalContext();
  const { updateComposer } = useComposer("compose-modal");

  const unmuteAccount = useUnmuteAccount();
  const blockAccount = useBlockAccount();
  const unblockAccount = useUnblockAccount();
  const getOrCreateChatByAccount = useGetOrCreateChatByAccount();

  if (!account) {
    return (
      <div>
        <div>
          <div className="relative h-32 w-full bg-gray-200 lg:h-48 dark:bg-gray-800" />
        </div>

        <div className="px-4 sm:px-6">
          <HStack alignItems="bottom" space={5} className="-mt-12">
            <div className="relative flex">
              <div className="size-24 rounded-full bg-gray-400 ring-4 ring-white dark:ring-gray-800" />
            </div>
          </HStack>
        </div>
      </div>
    );
  }

  const onBlock = () => {
    if (account.relationship?.blocking) {
      unblockAccount.mutate({ account });
    } else {
      openModal("CONFIRM", {
        heading: (
          <FormattedMessage
            id="confirmations.block.heading"
            defaultMessage="Block @{name}"
            values={{ name: account.acct }}
          />
        ),
        message: (
          <FormattedMessage
            id="confirmations.block.message"
            defaultMessage="Are you sure you want to block {name}?"
            values={{
              name: (
                <strong className="wrap-break-word">@{account.acct}</strong>
              ),
            }}
          />
        ),
        confirm: intl.formatMessage(messages.blockConfirm),
        onConfirm: () => blockAccount.mutate({ account }),
        secondary: intl.formatMessage(messages.blockAndReport),
        onSecondary: () => {
          blockAccount.mutate({ account });
          openModal("REPORT", {
            entityType: ReportableEntities.ACCOUNT,
            account,
          });
        },
      });
    }
  };

  const onMention = () => {
    openModal("COMPOSE");
    updateComposer({ text: `@${account.username} ` });
  };

  const onReport = () => {
    openModal("REPORT", { entityType: ReportableEntities.ACCOUNT, account });
  };

  const onMute = () => {
    if (account.relationship?.muting) {
      unmuteAccount.mutate({ account });
    } else {
      openModal("MUTE", { accountId: account.id });
    }
  };

  const onAvatarClick = () => {
    const avatar = {
      type: "image",
      url: account.avatar_static,
    } as Attachment;
    openModal("MEDIA", { media: [{ attachment: avatar }] });
  };

  const handleAvatarClick: React.MouseEventHandler = (e) => {
    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onAvatarClick();
    }
  };

  const handleShare = () => {
    navigator
      .share({
        text: `@${account.acct}`,
        url: account.url,
      })
      .catch((e) => {
        if (e.name !== "AbortError") console.error(e);
      });
  };

  const handleCopy: React.EventHandler<React.MouseEvent> = (e) => {
    copy(account.url, () => toast.success(messages.copied));
  };

  const makeMenu = () => {
    const menu: Menu = [];

    if (!account) {
      return [];
    }

    if ("share" in navigator) {
      menu.push({
        text: intl.formatMessage(messages.share, { name: account.username }),
        action: handleShare,
        icon: uploadIcon,
      });
    }

    menu.push({
      text: intl.formatMessage(messages.copy),
      action: handleCopy,
      icon: clipboardCopyIcon,
    });

    if (!currentAccount) return menu;

    if (menu.length) {
      menu.push(null);
    }

    if (account.id === currentAccount.id) {
      menu.push({
        text: intl.formatMessage(messages.edit_profile),
        to: "/settings/profile",
        icon: userIcon,
      });
      menu.push({
        text: intl.formatMessage(messages.preferences),
        to: "/settings",
        icon: settingsIcon,
      });
      menu.push(null);
      menu.push({
        text: intl.formatMessage(messages.mutes),
        to: "/mutes",
        icon: circleXIcon,
      });
      menu.push({
        text: intl.formatMessage(messages.blocks),
        to: "/blocks",
        icon: banIcon,
      });
    } else {
      menu.push({
        text: intl.formatMessage(messages.mention, { name: account.username }),
        action: onMention,
        icon: atIcon,
      });

      menu.push(null);

      if (account.relationship?.muting) {
        menu.push({
          text: intl.formatMessage(messages.unmute, { name: account.username }),
          action: onMute,
          icon: circleXIcon,
        });
      } else {
        menu.push({
          text: intl.formatMessage(messages.mute, { name: account.username }),
          action: onMute,
          icon: circleXIcon,
        });
      }

      if (account.relationship?.blocking) {
        menu.push({
          text: intl.formatMessage(messages.unblock, {
            name: account.username,
          }),
          action: onBlock,
          icon: banIcon,
        });
      } else {
        menu.push({
          text: intl.formatMessage(messages.block, { name: account.username }),
          action: onBlock,
          icon: banIcon,
        });
      }

      menu.push({
        text: intl.formatMessage(messages.report, { name: account.username }),
        action: onReport,
        icon: flagIcon,
      });
    }

    return menu;
  };

  const renderBadges = () => {
    if (!account || !currentAccount) return null;

    const isFollowedBy =
      currentAccount.id !== account.id && account.relationship?.followed_by;
    const isBlocking =
      currentAccount.id !== account.id && account.relationship?.blocking;
    const isMuting =
      currentAccount.id !== account.id && account.relationship?.muting;

    return (
      <div className="absolute right-4 bottom-3">
        <HStack alignItems="center" space={1}>
          {isFollowedBy && (
            <Badge variant="overlay">
              <FormattedMessage
                id="account.follows_you"
                defaultMessage="Follows you"
              />
            </Badge>
          )}

          {isBlocking && (
            <Badge variant="overlay">
              <FormattedMessage id="account.blocked" defaultMessage="Blocked" />
            </Badge>
          )}

          {isMuting && (
            <Badge variant="overlay">
              <FormattedMessage id="account.muted" defaultMessage="Muted" />
            </Badge>
          )}
        </HStack>
      </div>
    );
  };

  const renderSearchButton = () => {
    if (currentAccount?.id) {
      return (
        <IconButton
          src={searchIcon}
          onClick={() => navigate(`/@${account.username}/search`)}
          title={intl.formatMessage(messages.search, {
            name: account.username,
          })}
          variant="outline"
          className="self-stretch px-2"
          iconClassName="size-5"
        />
      );
    }
    return null;
  };

  const renderMessageButton = () => {
    if (account.accepting_messages) {
      if (!currentAccount || !account || account.id === currentAccount?.id) {
        return null;
      }

      const canChat = account.relationship?.followed_by;
      if (!canChat) {
        return null;
      }

      return (
        <IconButton
          src={messagesIcon}
          onClick={() => {
            getOrCreateChatByAccount.mutate(account.id, {
              onSuccess(response) {
                navigate(`/chats/${response.data.id}`);
              },
            });
          }}
          title={intl.formatMessage(messages.chat, { name: account.username })}
          variant="outline"
          className="self-stretch px-2"
          iconClassName="size-5"
          disabled={getOrCreateChatByAccount.isLoading}
        />
      );
    }

    return null;
  };

  const renderShareButton = () => {
    const canShare = "share" in navigator;

    if (
      !(
        account &&
        currentAccount?.id &&
        account.id === currentAccount?.id &&
        canShare
      )
    ) {
      return null;
    }

    return (
      <IconButton
        src={uploadIcon}
        onClick={handleShare}
        title={intl.formatMessage(messages.share, { name: account.username })}
        variant="outline"
        className="self-stretch px-2"
        iconClassName="size-5"
      />
    );
  };

  const menu = makeMenu();

  return (
    <div>
      <div className="relative isolate flex h-32 w-full flex-col justify-center overflow-hidden bg-gray-200 lg:h-48 dark:bg-gray-800">
        <HeaderImage account={account} />

        {renderBadges()}
      </div>

      <div className="px-4">
        <HStack className="-mt-10" alignItems="top" space={5}>
          <a
            href={account.avatar_static}
            onClick={handleAvatarClick}
            target="_blank"
          >
            <Avatar
              src={account.avatar_static}
              size={80}
              className="relative size-20 rounded-full bg-white ring-[5px] ring-white dark:bg-primary-900 dark:ring-gray-900"
            />
          </a>

          <div className="mt-14 -mb-4 flex w-full justify-end">
            <HStack space={2} alignItems="stretch">
              <SubscriptionButton account={account} />
              {renderSearchButton()}
              {renderMessageButton()}
              {renderShareButton()}

              {menu.length > 0 && (
                <DropdownMenu items={menu} placement="bottom-end">
                  <IconButton
                    src={dotsIcon}
                    variant="outline"
                    className="self-stretch px-2"
                    iconClassName="h-5 w-5"
                    children={null}
                  />
                </DropdownMenu>
              )}

              <ActionButton account={account} />
            </HStack>
          </div>
        </HStack>
      </div>
    </div>
  );
};

export default Header;
